import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, CircleAlert } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageTitle, Panel } from "@/components/admin/AdminLayout";
import { TelegramSetup } from "@/components/admin/TelegramSetup";
import { TelegramGoLive } from "@/components/admin/TelegramGoLive";

const Group = ({ g, onSaved }) => {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async (clear) => {
    setBusy(true);
    try {
      await api.put("/admin/integration-keys", { values: clear || vals });
      toast.success(clear ? "Cleared" : "Saved - active immediately"); setVals({}); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ready = g.fields.some((f) => f.is_set);
  return (
    <Panel title={g.name} testid={`keys-group-${g.key}`}>
      <p className={`text-xs font-semibold mb-3 flex items-center gap-1 ${ready ? "text-emerald-700" : "text-slate-500"}`}>
        {ready ? <CheckCircle2 size={14} /> : <CircleAlert size={14} />} {g.fields.filter((f) => f.is_set).length}/{g.fields.length} fields set
      </p>
      <div className="space-y-4">
        {g.fields.map((f) => (
          <label key={f.name} className="block space-y-1">
            <span className="text-sm font-semibold">{f.label}</span>
            <div className="flex gap-2">
              <input type={f.secret ? "password" : "text"} autoComplete="new-password" className="flex-1 h-10 rounded-md border border-slate-200 px-3 text-sm"
                placeholder={f.secret ? (f.is_set ? `Saved ${f.hint} - type to replace` : "Not set") : (f.value ? "Type a new value to replace" : f.default ? `Default: ${f.default}` : "Not set")}
                value={vals[f.name] ?? ""} onChange={(e) => setVals({ ...vals, [f.name]: e.target.value })} data-testid={`key-input-${f.name}`} />
              {f.source === "admin" && <button type="button" onClick={() => window.confirm(`Clear ${f.label}?`) && save({ [f.name]: "" })} className="px-3 rounded-md border text-xs text-red-600" data-testid={`key-clear-${f.name}`}>Clear</button>}
            </div>
            {!f.secret && f.value && <span className="text-xs block" data-testid={`key-current-${f.name}`}>Current: <code className="bg-slate-100 px-1 rounded break-all">{f.value}</code></span>}
            <span className="text-xs text-slate-500 block">{f.help}{f.source && ` · currently from ${f.source}`}</span>
          </label>
        ))}
      </div>
      <button disabled={busy || !Object.values(vals).some((v) => v.trim())} onClick={() => save()} className="mt-4 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold disabled:opacity-40" data-testid={`keys-save-${g.key}`}>Save {g.name}</button>
    </Panel>
  );
};

export default function IntegrationsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["integration-keys"], queryFn: () => api.get("/admin/integration-keys").then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["integration-keys"] }); qc.invalidateQueries({ queryKey: ["social"] }); qc.invalidateQueries({ queryKey: ["integrations"] }); };
  return (
    <div className="space-y-6" data-testid="admin-integrations-page">
      <PageTitle title="Integrations & API keys" sub="Paste keys here - no files to edit. Secret keys are stored encrypted and are never shown again (only the last 4 characters). Changes work immediately." />
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex gap-2" data-testid="keys-next-steps">
        <KeyRound size={18} className="shrink-0 mt-0.5" />
        <div>After saving: switch each platform on in <Link className="underline font-semibold" to="/admin/settings">Settings → Social posting</Link>; connect Pinterest and pick a board on the <Link className="underline font-semibold" to="/admin/social">Social page</Link>; add new affiliate platforms with their own feed in <Link className="underline font-semibold" to="/admin/catalog">Catalog → Networks</Link>.</div>
      </div>
      <TelegramSetup onChanged={refresh} />
      <TelegramGoLive />
      <div className="grid lg:grid-cols-2 gap-6">{(data || []).map((g) => <Group key={g.key} g={g} onSaved={refresh} />)}</div>
    </div>
  );
}
